import jobcostingService from '../../../services/jobcostingService';
import { loadDeferredTotal } from '../../../utils/deferredTotal';
import filterService from '../../../services/filterService';

import {
    setJobCostingLoading,
    setJobCostingError,
    setJobCostingList,
    setJobCostingCurrent,
    setJobCostingPagination,
    setJobCostingSort,
    setJobCostingFilters,
    setJobCostingDefaultFilter,
    setJobCostingSettings,
    clearJobCostingCurrent,
    clearJobCostingError,
    setJobCostingFilterLoading,
    changeJobCostingFilterCondition,
    addJobCostingFilterCondition,
    updateJobCostingFilterConditionRule,
    updateJobCostingFilterConditionValue,
    removeJobCostingFilterCondition,
    setJobCostingSelected,
    resetJobCostingFiltersAndPagination
} from './jobcostingSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().jobcosting.isSettingsInitialized) {
            dispatch(setJobCostingLoading(true));
            const response = await jobcostingService.getSettings();

            // Set the settings
            dispatch(setJobCostingSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setJobCostingDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setJobCostingError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setJobCostingLoading(false));
    }
};

export const fetchJobcostings = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().jobcosting;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setJobCostingLoading(true));
        const response = await jobcostingService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setJobCostingList(response));
        loadDeferredTotal(response, jobcostingService, finalQueryParams, finalActiveFilter, {}, dispatch, setJobCostingPagination);
        return response;
    } catch (error) {
        console.error('Error in fetchJobcostings:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setJobCostingError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobCostingLoading(false));
    }
};

export const fetchJobCosting = (id) => async (dispatch) => {
    try {
        dispatch(setJobCostingLoading(true));
        const response = await jobcostingService.getOne(id);
        dispatch(setJobCostingCurrent(response.data));
    } catch (error) {
        dispatch(setJobCostingError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setJobCostingLoading(false));
    }
};

export const createJobCosting = (data) => async (dispatch) => {
    try {
        const response = await jobcostingService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateJobCosting = (id, data) => async (dispatch) => {
    try {
        const response = await jobcostingService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteJobCosting = (id) => async (dispatch) => {
    try {
        dispatch(setJobCostingLoading(true));
        const response = await jobcostingService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setJobCostingError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobCostingLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setJobCostingPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setJobCostingSort(sort));
};

export const searchJobcostings = (query) => (dispatch, getState) => {
    const currentFilters = getState().jobcosting.filters;
    dispatch(setJobCostingFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setJobCostingDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeJobCostingFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addJobCostingFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateJobCostingFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateJobCostingFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeJobCostingFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setJobCostingFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().jobcosting.settings;
            dispatch(setJobCostingSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setJobCostingFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setJobCostingFilterLoading(false));
        dispatch(setJobCostingError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setJobCostingSelected(selectedRows));
    };
};

export const clearCurrentJobCosting = () => {
    return (dispatch) => {
        dispatch(clearJobCostingCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetJobCostingFiltersAndPagination());
};